import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssignmentIcon from "@mui/icons-material/Assignment";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { Button, Grid, Paper, styled, Typography } from "@mui/material";
import { api, types } from "api";
import crown from "assets/icons/crown.svg";
import team from "assets/icons/team.svg";
import { AssignmentModelList } from "components/AssignmentModelList";
import { InfoSnackBar } from "components/InfoSnackBar";
import { Assignment, getStatus, Status } from "helpers/assignment";
import { useBreadcrumbs } from "helpers/useBreadcrumbs";
import { ACTIVITIES_FETCH_ERR } from "messages";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { routes } from "routes";

const PREFIX = "StudentGroupDetailPage";

const classes = {
	root: `${PREFIX}-root`,
	title: `${PREFIX}-title`,
	summaryCard: `${PREFIX}-summaryCard`,
	summaryTitle: `${PREFIX}-summaryTitle`,
	summaryText: `${PREFIX}-summaryText`,
	leaderboard: `${PREFIX}-leaderboard`,
	teamLogo: `${PREFIX}-teamLogo`,
	icon: `${PREFIX}-icon`,
};

const Root = styled("div")(({ theme }) => ({
	[`&.${classes.root}`]: {
		padding: 20,
	},

	[`& .${classes.title}`]: {
		marginTop: 5,
		marginBottom: 15,
		float: "left",
	},

	[`& .${classes.summaryCard}`]: {
		marginTop: 30,
		height: 200,
		padding: theme.spacing(2),
		minWidth: 200,
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(2),
	},

	[`& .${classes.summaryTitle}`]: {
		color: theme.palette.text.secondary,
		textAlign: "center",
		fontSize: 16,
		paddingTop: 15,
		marginBottom: 25,
	},

	[`& .${classes.summaryText}`]: {
		fontSize: 16,
		textAlign: "center",
		fontWeight: "bold",
		marginTop: 15,
		marginBottom: 25,
	},

	[`& .${classes.leaderboard}`]: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
	},

	[`& .${classes.teamLogo}`]: {
		width: "100%",
	},

	[`& .${classes.icon}`]: {
		color: theme.palette.warning.main,
	},
}));

export const StudentGroupDetailPage = (): JSX.Element => {
	const [assignments, setAssignment] = useState<Assignment[]>([]);
	const { updateLastBreadcrumbs } = useBreadcrumbs();
	const [err, setErr] = useState(false);
	const params = useParams<{ groupId: types.UUID }>();

	const componentMounted = useRef(true);

	const stateCount = (state: Status): number => {
		let count = 0;
		assignments.forEach((el) => {
			if (el.status === state) {
				count += 1;
			}
		});
		return count;
	};

	useEffect(() => {
		api
			.getGroupAssignments(params.groupId)
			.then((res) => {
				if (res.status !== 200) {
					setErr(true);
					return;
				}

				if (componentMounted.current) {
					const now = new Date();
					setAssignment(
						(res.data || []).map((ass) => ({
							...ass,
							status: getStatus(ass, now),
						})),
					);
				}
			})
			.catch(() => {
				setErr(true);
			});
		api
			.getGroup(params.groupId)
			.then((res) => {
				if (res.status !== 200) {
					setErr(true);
					return;
				}

				if (componentMounted.current) {
					updateLastBreadcrumbs(res.data.name);
				}
			})
			.catch(() => {
				setErr(true);
			});
	}, [params, updateLastBreadcrumbs]);

	return (
		<Root className={classes.root}>
			<Grid container spacing={2}>
				<Grid item md={7} xs={12}>
					<Grid container spacing={2}>
						<Paper className={classes.summaryCard}>
							<Typography className={classes.summaryTitle}>
								COMPLETED ASSIGNMENTS
							</Typography>
							<Typography className={classes.summaryText}>
								{stateCount("Completed")}
							</Typography>
							<Grid container justifyContent="space-around">
								<EmojiEventsIcon
									data-testid="champion"
									fontSize="large"
									className={classes.icon}
								/>
							</Grid>
						</Paper>
						<Paper className={classes.summaryCard}>
							<Typography className={classes.summaryTitle}>
								SCHEDULED ASSIGNMENTS
							</Typography>
							<Typography className={classes.summaryText}>
								{stateCount("Scheduled")}
							</Typography>
							<Grid container justifyContent="space-around">
								<AccessTimeIcon
									data-testid="time"
									fontSize="large"
									className={classes.icon}
								/>
							</Grid>
						</Paper>
						<Paper className={classes.summaryCard}>
							<Typography className={classes.summaryTitle}>
								ON-GOING ASSIGNMENTS
							</Typography>
							<Typography className={classes.summaryText}>
								{stateCount("In Progress")}
							</Typography>
							<Grid container justifyContent="space-around">
								<AssignmentIcon
									data-testid="assignments"
									fontSize="large"
									className={classes.icon}
								/>
							</Grid>
						</Paper>
					</Grid>
				</Grid>
				<Grid
					item
					md={5}
					xs={12}
					container
					flexDirection="column"
					alignItems="flex-end"
				>
					<Button
						variant="contained"
						color="primary"
						component={Link}
						to={routes._.groups._.view._.leaderboard.path.replace(
							":groupId",
							params.groupId,
						)}
						className={classes.leaderboard}
					>
						<img
							data-testid="crown"
							src={crown}
							alt="crown"
							style={{ maxHeight: 40, maxWidth: 40 }}
						/>
						Leaderboard
					</Button>

					<img
						className={classes.teamLogo}
						data-testid="team"
						src={team}
						alt="team"
						style={{ maxHeight: 300, maxWidth: 500 }}
					/>
				</Grid>

				<Grid item xs={12}>
					<Typography className={classes.title} variant="h4">
						Assignments
					</Typography>
				</Grid>

				<Grid item xs={12}>
					{assignments.length === 0 ? (
						<Typography variant="h5" className={classes.title}>
							No ongoing assignments
						</Typography>
					) : (
						<AssignmentModelList assignments={assignments} show_csv={false} />
					)}
				</Grid>
			</Grid>

			<InfoSnackBar
				isOpen={err}
				onClose={() => setErr(false)}
				message={ACTIVITIES_FETCH_ERR}
				severity="error"
			/>
		</Root>
	);
};
